// ===================================
// SETTINGS CONTEXT - src/contexts/SettingsContext.tsx
// ===================================
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface SettingsContextType {
  hideChatSidebar: boolean;
  setHideChatSidebar: (value: boolean) => void;
  hideChatMode: boolean;
  setHideChatMode: (value: boolean) => void;
  hideSearch: boolean;
  setHideSearch: (value: boolean) => void;
  hideExport: boolean;
  setHideExport: (value: boolean) => void;
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  notifications: boolean;
  setNotifications: (value: boolean) => void;
  autoSaveChats: boolean;
  setAutoSaveChats: (value: boolean) => void;
  compactMode: boolean;
  setCompactMode: (value: boolean) => void;
  debugMode: boolean;
  setDebugMode: (value: boolean) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

const SETTINGS_STORAGE_KEY = "enterprise-rag-settings";

interface Settings {
  hideChatSidebar: boolean;
  hideChatMode: boolean;
  hideSearch: boolean;
  hideExport: boolean;
  darkMode: boolean;
  notifications: boolean;
  autoSaveChats: boolean;
  compactMode: boolean;
  debugMode: boolean;
}

const DEFAULT_SETTINGS: Settings = {
  hideChatSidebar: false,
  hideChatMode: false,
  hideSearch: false,
  hideExport: false,
  darkMode: true,
  notifications: false,
  autoSaveChats: true,
  compactMode: false,
  debugMode: false,
};

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
}) => {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load settings from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      }
    } catch (error) {
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    if (!isLoaded) return;
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {}
  }, [settings, isLoaded]);

  // Generic setter
  const setSetting = (key: keyof Settings, value: boolean) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const setDarkMode = (value: boolean) => {
    setSetting("darkMode", value);
    if (value) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  };

  const value: SettingsContextType = {
    ...settings,
    setHideChatSidebar: (v) => setSetting("hideChatSidebar", v),
    setHideChatMode: (v) => setSetting("hideChatMode", v),
    setHideSearch: (v) => setSetting("hideSearch", v),
    setHideExport: (v) => setSetting("hideExport", v),
    setDarkMode,
    setNotifications: (v) => setSetting("notifications", v),
    setAutoSaveChats: (v) => setSetting("autoSaveChats", v),
    setCompactMode: (v) => setSetting("compactMode", v),
    setDebugMode: (v) => setSetting("debugMode", v),
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8b3dff]"></div>
      </div>
    );
  }

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
